import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type PopupType = 'info' | 'success' | 'warning' | 'error' | 'confirm' | 'custom';
export type PopupSize = 'small' | 'medium' | 'large' | 'fullscreen';
export type PopupSeverity = 'primary' | 'secondary' | 'success' | 'danger' | 'warn' | 'info';

export interface PopupButton {
  label: string;
  severity?: PopupSeverity;
  outlined?: boolean;
  callback: () => void | Promise<void>;
  loading?: boolean;
}

export interface PopupConfig {
  visible: boolean;
  type?: PopupType;
  title?: string;
  message?: string;
  icon?: string;
  size?: PopupSize;
  closable?: boolean;
  dismissable?: boolean;
  buttons?: PopupButton[];
  customTemplate?: any;
  data?: any;
  /** Runs when the user closes the dialog itself (X button, mask click, Esc), not when a button or `close()` does. */
  onClose?: () => void;
}

const defaultConfig: PopupConfig = {
  visible: false,
  type: 'info',
  size: 'medium',
  closable: true,
  dismissable: true,
  buttons: []
};

/**
 * App-wide popup service for showing dialogs.
 */
@Injectable({
  providedIn: 'root'
})
export class AppPopupService {
  private readonly _state$ = new BehaviorSubject<PopupConfig>(defaultConfig);

  readonly state$: Observable<PopupConfig> = this._state$.asObservable();

  /**
   * Open a custom popup with full configuration
   */
  open(config: Partial<PopupConfig>): void {
    this._state$.next({
      ...defaultConfig,
      ...config,
      visible: true
    });
  }

  /**
   * Show an info popup
   */
  info(title: string, message: string, onOk?: () => void): void {
    this.open({
      type: 'info',
      title,
      message,
      icon: 'pi pi-info-circle',
      buttons: [
        {
          label: 'OK',
          severity: 'primary',
          callback: () => {
            onOk?.();
            this.close();
          }
        }
      ]
    });
  }

  /**
   * Show a success popup
   */
  success(title: string, message: string, onOk?: () => void): void {
    this.open({
      type: 'success',
      title,
      message,
      icon: 'pi pi-check-circle',
      buttons: [
        {
          label: 'OK',
          severity: 'success',
          callback: () => {
            onOk?.();
            this.close();
          }
        }
      ]
    });
  }

  /**
   * Show a warning popup
   */
  warning(title: string, message: string, onOk?: () => void): void {
    this.open({
      type: 'warning',
      title,
      message,
      icon: 'pi pi-exclamation-triangle',
      buttons: [
        {
          label: 'OK',
          severity: 'warn',
          callback: () => {
            onOk?.();
            this.close();
          }
        }
      ]
    });
  }

  /**
   * Show an error popup
   */
  error(title: string, message: string, onOk?: () => void): void {
    this.open({
      type: 'error',
      title,
      message,
      icon: 'pi pi-times-circle',
      buttons: [
        {
          label: 'OK',
          severity: 'danger',
          callback: () => {
            onOk?.();
            this.close();
          }
        }
      ]
    });
  }

  /**
   * Show a confirmation dialog
   */
  confirm(
    title: string,
    message: string,
    onConfirm: () => void | Promise<void>,
    onCancel?: () => void,
    options?: {
      confirmLabel?: string;
      cancelLabel?: string;
      confirmSeverity?: PopupSeverity;
      icon?: string;
      size?: PopupSize;
      /** Runs when the user dismisses the dialog with the X button instead of choosing a button. */
      onDismiss?: () => void;
    }
  ): void {
    this.open({
      type: 'confirm',
      title,
      message,
      icon: options?.icon ?? 'pi pi-question-circle',
      size: options?.size ?? defaultConfig.size,
      closable: true,
      dismissable: false,
      onClose: options?.onDismiss,
      buttons: [
        {
          label: options?.cancelLabel ?? 'Cancel',
          severity: 'secondary',
          outlined: true,
          callback: () => {
            onCancel?.();
            this.close();
          }
        },
        {
          label: options?.confirmLabel ?? 'Confirm',
          severity: options?.confirmSeverity ?? 'primary',
          callback: async () => {
            await onConfirm();
            this.close();
          }
        }
      ]
    });
  }

  /**
   * Close the current popup
   */
  close(): void {
    this._state$.next({ ...defaultConfig, visible: false });
  }
}
